"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, MessageCircle, Send, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="bg-secondary/20 border border-secondary rounded-xl p-8 text-center animate-in fade-in zoom-in duration-500">
        <div className="mx-auto bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mb-4">
          <Send className="text-primary size-8" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-primary mb-2">Messaggio Inviato!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="nome" rules={{
      required: "Il nome è obbligatorio"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Nome</FormLabel>
            <FormControl>
              <Input placeholder="Mario Rossi" className="bg-background/50 border-primary/20 focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "L'email è obbligatoria",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Inserisci un indirizzo email valido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Indirizzo Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="mario@example.com" className="bg-background/50 border-primary/20 focus:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="telefono" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Telefono (opzionale)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+39 339 1234567" className="bg-background/50 border-primary/20 focus:border-primary" {...field} />
            </FormControl>
            <FormDescription className="text-xs">
              Inserisci il numero se vuoi essere contattato telefonicamente.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="messaggio" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Messaggio</FormLabel>
            <FormControl>
              <Textarea placeholder="Scrivi il tuo messaggio..." className="bg-background/50 border-primary/20 focus:border-primary min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consenso" rules={{
      required: "Devi acconsentire al trattamento dei dati"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md p-2">
            <FormControl>
              <input type="checkbox" checked={value} onChange={onChange} className="mt-1 size-4 rounded border-primary text-primary focus:ring-primary accent-primary cursor-pointer" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal cursor-pointer">
                Acconsento al trattamento dei dati personali
              </FormLabel>
              <FormDescription className="text-xs">
                Ai sensi del GDPR, i tuoi dati saranno usati solo per rispondere alla tua richiesta.
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Invio in corso...
          </> : "Invia Messaggio"}
      </Button>
    </Form>;
}
export default function RimaniamoInContatto() {
  return <section id="rimaniamo-in-contatto" className="relative py-20 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-secondary/20 to-transparent pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="flex flex-col justify-center space-y-10 pt-4">
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-primary tracking-tight">
                Rimaniamo <br />
                <span className="text-primary/80 italic">in Contatto</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Siamo qui per rispondere a tutte le tue domande sui nostri dolci artigianali. 
                Vieni a trovarci o scrivici per ordinare la tua torta personalizzata.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start gap-5 group">
                <div className="mt-1 bg-secondary/30 p-3 rounded-full text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <MapPin className="size-6" />
                </div>
                <div>
                  <h4 className="font-serif text-xl font-semibold text-primary mb-1">Vieni a trovarci</h4>
                  <p className="text-muted-foreground text-lg">Via dei Cappuccini, 31, 92019 Sciacca AG, Italy</p>
                  <Link to="https://maps.app.goo.gl/MNFsirdEqw6RuVf96" newTab className="text-sm text-primary/70 hover:text-primary underline decoration-primary/30 underline-offset-4 mt-1 inline-block">
                    Vedi su mappa
                  </Link>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="mt-1 bg-secondary/30 p-3 rounded-full text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Phone className="size-6" />
                </div>
                <div>
                  <h4 className="font-serif text-xl font-semibold text-primary mb-1">Chiamaci</h4>
                  <p className="text-muted-foreground text-lg font-medium">+39 0925 86986</p>
                  <p className="text-sm text-muted-foreground/80 mt-1">Siamo disponibili per ordini telefonici.</p>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="mt-1 bg-secondary/30 p-3 rounded-full text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Mail className="size-6" />
                </div>
                <div>
                  <h4 className="font-serif text-xl font-semibold text-primary mb-1">Scrivici</h4>
                  <p className="text-muted-foreground text-lg">danielasabella099@gmail.com</p>
                </div>
              </div>

              <div className="flex items-start gap-5 group">
                <div className="mt-1 bg-secondary/30 p-3 rounded-full text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Clock className="size-6" />
                </div>
                <div>
                  <h4 className="font-serif text-xl font-semibold text-primary mb-1">Orari di Apertura</h4>
                  <p className="text-muted-foreground text-lg">Mar‑Sab: 7:30 – 19:00</p>
                  <p className="text-sm text-muted-foreground/80 mt-1">Domenica: 7:30 – 14:00 (Lunedì Chiuso)</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-primary/10">
              <h4 className="font-serif text-lg font-medium text-primary mb-4">Seguici sui social</h4>
              <div className="flex gap-4">
                <Link to="https://facebook.com" newTab className="bg-white p-3 rounded-full shadow-sm hover:shadow-md hover:bg-primary hover:text-primary-foreground transition-all duration-300 text-primary border border-primary/10" aria-label="Facebook">
                  <Facebook className="size-5" />
                </Link>
                <Link to="https://instagram.com" newTab className="bg-white p-3 rounded-full shadow-sm hover:shadow-md hover:bg-primary hover:text-primary-foreground transition-all duration-300 text-primary border border-primary/10" aria-label="Instagram">
                  <Instagram className="size-5" />
                </Link>
              </div>
            </div>
          </div>

          {}
          <div className="lg:mt-10">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-primary/5 border border-primary/10 p-6 md:p-8 lg:p-10">
              <div className="mb-8">
                <h3 className="text-2xl font-serif font-bold text-primary mb-2">Inviaci un messaggio</h3>
                <p className="text-muted-foreground">Compila il modulo per richiedere informazioni o prenotare i tuoi dolci preferiti.</p>
              </div>
              
              <FormProvider formId="contact_form_contact" sectionName="rimaniamo-in-contatto" defaultValues={{
              nome: "",
              email: "",
              telefono: "",
              messaggio: "",
              consenso: false
            }} validationRules={{
              nome: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              consenso: {
                required: true
              }
            }} submitText="Invia Messaggio" successMessage="Grazie! Abbiamo ricevuto il tuo messaggio e ti risponderemo al più presto." errorMessage="Si è verificato un errore durante l'invio. Per favore riprova.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>

      {}
      <Link to="https://wa.me/393402498369" newTab className="fixed bottom-6 right-6 z-50 flex items-center justify-center size-14 rounded-full bg-chart-4 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-chart-4" aria-label="Contattaci su WhatsApp">
        <MessageCircle className="size-8 fill-current" />
      </Link>
    </section>;
}